/**
 * TemperatureSensorModalClassic — Monolithic temperature sensor modal template.
 *
 * Read-only temperature sensor display panel:
 * - Temperature: large °C display with thermometer icon
 * - Range: min/max/avg if available
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './TemperatureSensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const TEMPERATURE_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Temperature Section ──
  { id: 'temperature', label: 'Temperature Reading', icon: 'thermostat', slots: [
    { id: 'temp-label', label: 'Title "TEMPERATURE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'temp-icon', label: 'Thermometer Icon', editableProps: ['fontSize', 'color'] },
    { id: 'temp-value', label: 'Temperature Number (large)', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'temp-unit', label: 'Unit "°C"', editableProps: ['fontSize', 'color'] },
    { id: 'temp-status', label: 'Temperature Status Text', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Range Section ──
  { id: 'range', label: 'Temperature Range', icon: 'expand', slots: [
    { id: 'range-label', label: 'Title "RANGE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'range-card', label: 'Range Card Container', editableProps: ['background', 'borderRadius', 'padding', 'border'] },
    { id: 'range-card-icon', label: 'Range Card Icon', editableProps: ['fontSize', 'color'] },
    { id: 'range-card-label', label: 'Range Card Label', editableProps: ['fontSize', 'color'] },
    { id: 'range-card-value', label: 'Range Card Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
]

const DEFAULT_SECTION_ORDER = TEMPERATURE_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Temperature status helpers
// ---------------------------------------------------------------------------

function getTempColor(temp: number): string {
  if (temp >= 35) return '#ef4444'
  if (temp >= 28) return '#f97316'
  if (temp >= 18) return '#06b6d4'
  if (temp >= 10) return '#60a5fa'
  return '#93c5fd'
}

function getTempStatus(temp: number): string {
  if (temp >= 35) return 'Very Hot'
  if (temp >= 28) return 'Warm'
  if (temp >= 22) return 'Comfortable'
  if (temp >= 18) return 'Cool'
  if (temp >= 10) return 'Cold'
  return 'Very Cold'
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TemperatureSensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const rawTemp = numAttr(deviceState, 'temperatureAmbientCelsius', 'temperature', 'currentTemperature')
  const displayTemp = rawTemp ?? (isEditorMode ? 24.5 : null)

  const minTemp = numAttr(deviceState, 'temperatureMin', 'minTemperature')
  const maxTemp = numAttr(deviceState, 'temperatureMax', 'maxTemperature')
  const avgTemp = numAttr(deviceState, 'temperatureAvg', 'avgTemperature')

  const tempColor = useMemo(() => displayTemp !== null ? getTempColor(displayTemp) : '#06b6d4', [displayTemp])
  const tempStatus = useMemo(() => displayTemp !== null ? getTempStatus(displayTemp) : null, [displayTemp])

  const rangeCards = useMemo(() => {
    const cards: Array<{ key: string; label: string; icon: string; value: number | null; color: string }> = []
    if (minTemp !== null || isEditorMode) {
      cards.push({ key: 'min', label: 'Min', icon: 'keyboard_arrow_down', value: minTemp ?? (isEditorMode ? 18.2 : null), color: '#60a5fa' })
    }
    if (avgTemp !== null || isEditorMode) {
      cards.push({ key: 'avg', label: 'Avg', icon: 'drag_handle', value: avgTemp ?? (isEditorMode ? 23.1 : null), color: '#06b6d4' })
    }
    if (maxTemp !== null || isEditorMode) {
      cards.push({ key: 'max', label: 'Max', icon: 'keyboard_arrow_up', value: maxTemp ?? (isEditorMode ? 28.7 : null), color: '#f97316' })
    }
    return cards
  }, [minTemp, maxTemp, avgTemp, isEditorMode])

  return (
    <div
      className={`temperature-sensor-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="temperature-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="temperature-sensor-modal-classic__header">
        <button className="temperature-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="temperature-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Temperature Sensor'}</h2>
        <button
          className="temperature-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'temperature':
            return (
              <div key="temperature" className={`temperature-sensor-modal-classic__temperature${hl}`} data-modal-section="temperature">
                <div className="temperature-sensor-modal-classic__section-label" data-modal-slot="temp-label">TEMPERATURE</div>
                <div className="temperature-sensor-modal-classic__temp-display">
                  <span
                    className="material-symbols-rounded temperature-sensor-modal-classic__temp-icon"
                    data-modal-slot="temp-icon"
                    style={{ color: tempColor }}
                  >
                    thermostat
                  </span>
                  <div className="temperature-sensor-modal-classic__temp-number">
                    <span
                      className="temperature-sensor-modal-classic__temp-value"
                      data-modal-slot="temp-value"
                      style={{ color: tempColor }}
                    >
                      {displayTemp !== null ? displayTemp.toFixed(1) : '—'}
                    </span>
                    <span className="temperature-sensor-modal-classic__temp-unit" data-modal-slot="temp-unit">°C</span>
                  </div>
                  {tempStatus && (
                    <span
                      className="temperature-sensor-modal-classic__temp-status"
                      data-modal-slot="temp-status"
                      style={{ color: tempColor }}
                    >
                      {tempStatus}
                    </span>
                  )}
                </div>
              </div>
            )
          case 'range':
            if (rangeCards.length === 0) return null
            return (
              <div key="range" className={`temperature-sensor-modal-classic__range${hl}`} data-modal-section="range">
                <div className="temperature-sensor-modal-classic__section-label" data-modal-slot="range-label">RANGE</div>
                <div className="temperature-sensor-modal-classic__range-grid">
                  {rangeCards.map((card) => (
                    <div key={card.key} className="temperature-sensor-modal-classic__range-card" data-modal-slot="range-card">
                      <span
                        className="material-symbols-rounded temperature-sensor-modal-classic__range-icon"
                        data-modal-slot="range-card-icon"
                        style={{ color: card.color }}
                      >
                        {card.icon}
                      </span>
                      <span className="temperature-sensor-modal-classic__range-label" data-modal-slot="range-card-label">
                        {card.label}
                      </span>
                      <span className="temperature-sensor-modal-classic__range-value" data-modal-slot="range-card-value">
                        {card.value !== null ? `${card.value.toFixed(1)}°C` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
